/**
 * SoundController — headless. Renders nothing; watches the store and fires
 * cues on the transitions defined in {@link ./transitions.ts}.
 *
 * Responsibilities:
 *   • queue pop  — matchId null → non-null.
 *   • victory / defeat — edge into phase "ended".
 *   • countdown  — one pitched tick per whole second during "countdown",
 *     then a "go" at zero. Deduped so re-renders don't double-fire a tick.
 *
 * Ability-use is fired from the input layer (a local keypress), not here.
 */

import { useEffect, useRef, type ReactElement } from "react";
import { useStore } from "../store/context.js";
import { useSound } from "./SoundProvider.js";
import { countdownTickCue } from "./cues.js";
import { countdownCue, phaseTransitionCue, type SoundView } from "./transitions.js";
import { ticksToSeconds } from "../ui/hud-util.js";

const TICK_RATE = 30;

function viewOf(s: ReturnType<typeof useStore>["state"]): SoundView {
  return {
    phase: s.phase.phase,
    winner: s.phase.winner === null ? null : Number(s.phase.winner),
    ownSeat: s.ownSeat,
    matchId: s.matchId,
  };
}

export function SoundController(): ReactElement {
  const { state } = useStore();
  const sound = useSound();

  // Previous view for edge detection across renders.
  const prevRef = useRef<SoundView>(viewOf(state));
  // Last whole-second we ticked, so each second fires once.
  const lastTickSecondRef = useRef<number | null>(null);

  // Phase / match transitions → one-shot cues.
  useEffect(() => {
    const prev = prevRef.current;
    const next = viewOf(state);
    const decision = phaseTransitionCue(prev, next);
    if (decision !== null) sound.play(decision.cue);
    prevRef.current = next;
  }, [state, sound]);

  // Countdown ticks. #276: reads the SERVER's authoritative deadline
  // (`state.phase.endsAtTick`, mirrored from the sim's own
  // `PhaseCountdown.endsAtTick`) instead of fabricating one from the first
  // countdown snapshot observed — the same fix as `Countdown.tsx`. Emits one
  // cue per whole-second boundary as it counts down.
  useEffect(() => {
    if (state.phase.phase !== "countdown") {
      lastTickSecondRef.current = null;
      return;
    }
    const snap = state.hudSnapshot;
    if (snap === null) return;
    const endsAtTick = state.phase.endsAtTick ?? snap.tick;
    const seconds = ticksToSeconds(endsAtTick, snap.tick, TICK_RATE);
    if (lastTickSecondRef.current === seconds) return;
    lastTickSecondRef.current = seconds;

    const cue = countdownCue(seconds);
    if (cue === null) return;
    if (cue.kind === "go") {
      sound.play("countdownGo");
    } else {
      sound.playCue(countdownTickCue(cue.freq));
    }
  }, [state.phase.phase, state.hudSnapshot, sound]);

  return <></>;
}
